import { html, unsafeCSS, type PropertyValues } from "lit";
import { customElement, property } from "lit/decorators.js";
import { SlotController } from "@/common/controllers/slot";
import EkoElement from "@/utilities/element";
import style from "./dropdown-header.css?inline";

@customElement("eko-dropdown-header")
export class DropdownHeader extends EkoElement {
  static styles = [...EkoElement.styles, unsafeCSS(style)];

  private afterSlot = new SlotController(this, "after");

  private handleAppendSlotChange = () => {
    this.setBadgeSizes();
  };

  private setAfterAttribute = () => {
    this.setAttribute("after", this.afterSlot.tagName ?? "");
  };

  @property({ reflect: true }) padding: "md" | "sm" = "md";

  /**
   * Sets default size on badges in the append slot if they don't already have a size set.
   * This is SSR-safe as it runs after hydration in firstUpdated.
   */
  private setBadgeSizes() {
    // Query for badges that are directly slotted into append slot
    const directBadges = Array.from(
      this.querySelectorAll<HTMLElement & { size?: string }>('[slot="append"]')
    ).filter((el) => el.tagName.toLowerCase() === "eko-badge") as Array<
      HTMLElement & { size?: string }
    >;
    // Query for badges nested within elements slotted into append slot
    const nestedBadges = this.querySelectorAll<HTMLElement & { size?: string }>(
      '[slot="append"] eko-badge'
    );

    // Combine and deduplicate using Set
    const allBadges = new Set([...directBadges, ...nestedBadges]);
    allBadges.forEach((badge) => {
      // Only set size if it's not already set (respects user-provided values)
      if (!badge.hasAttribute("size")) {
        badge.size = "sm";
      }
    });
  }

  // SSR-safe: runs after initial render and hydration
  firstUpdated() {
    this.setBadgeSizes();
    this.setAfterAttribute();
  }

  render() {
    return html`
      <div class="eko-dropdown-header__content" part="content">
        <slot></slot>
        <slot name="append" @slotchange=${this.handleAppendSlotChange}></slot>
      </div>
      <slot name="after" @slotchange=${this.setAfterAttribute}></slot>
    `;
  }

  updated(changedProperties: PropertyValues) {
    if (changedProperties.has("after")) {
      this.setAfterAttribute();
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "eko-dropdown-header": DropdownHeader;
  }
}

export default DropdownHeader;
